import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalService } from 'src/app/_services/global.service';
import { BusinessSetupProgress, BusinessSetupProgressService } from 'src/app/_services/business-setup-progress.service';
import { UserService } from 'src/app/_services/user.service';

type WelcomeSetupStepKey = 'channels' | 'business' | 'domain' | 'product' | 'shipping' | 'stripe' | 'card';

interface WelcomeSetupStep {
  key: WelcomeSetupStepKey;
  title: string;
  description: string;
}

@Component({
  selector: 'app-welcome-setup-modal',
  templateUrl: './welcome-setup-modal.component.html',
  styleUrls: ['./welcome-setup-modal.component.scss']
})
export class WelcomeSetupModalComponent implements OnInit {
  private readonly storageKeyPrefix = 'welcome-setup-modal-seen-';

  visible = false;
  isLoadingProgress = false;
  private progress: BusinessSetupProgress | null = null;

  readonly steps: WelcomeSetupStep[] = [
    { key: 'channels', title: 'Sales channels', description: 'Confirm where you sell — online, in-store, or both.' },
    { key: 'business', title: 'Business information', description: 'Legal name, address and business type for receipts and payouts.' },
    { key: 'domain', title: 'Store domain & template', description: 'Claim your free subdomain and pick a storefront design.' },
    { key: 'product', title: 'Your first product', description: 'Add a product with photos, cost and price so you can test a sale.' },
    { key: 'shipping', title: 'Shipping', description: 'Set a flat rate, free-shipping threshold and local pickup.' },
    { key: 'stripe', title: 'Connect Stripe', description: 'Accept cards, wallets and in-person payments, and get paid out.' },
    { key: 'card', title: 'Billing card', description: 'Add a card on file to keep your online store subscription active.' }
  ];

  constructor(
    private readonly router: Router,
    private readonly businessSetupProgressService: BusinessSetupProgressService,
    private readonly userService: UserService
  ) {}

  ngOnInit(): void {
    const eligible = !this.hasBeenSeen() && !this.isSetupGuideHiddenForUser();
    if (!eligible) return;

    // First time this merchant lands on the dashboard after signup — if they already picked
    // in-store selling (signup-v6's "Where do you want to sell?" step), the business is
    // effectively fully set up with nothing further required, so flip IsBusinessSetup now
    // instead of waiting on the online-store-only checklist steps that don't apply to them.
    this.userService.completeBusinessSetupForInStoreChannel();
    // Resolve progress BEFORE showing anything — flipping `visible` true then immediately back to
    // false once a fully-complete merchant's progress arrives produced a visible flash of the
    // dialog opening and closing. A returning user (e.g. reopened via a "setup guide" link) may
    // have already finished some steps elsewhere — reflect that here instead of always showing
    // every step as untouched.
    this.isLoadingProgress = true;
    // Business info (address/city/state/zip) is read synchronously from the signed-in user, and
    // that step always counts — so when it's missing the merchant can't be fully set up and the
    // open-then-close flash can't happen. Open right away with skeleton rows instead of waiting on
    // the slowest of the progress requests (products / shipping / paymentconfig / Stripe status).
    if (this.isBusinessInfoMissing()) {
      this.visible = true;
    }
    this.businessSetupProgressService.getProgress().subscribe({
      next: (progress) => {
        this.progress = progress;
        this.isLoadingProgress = false;
        // Every step already done (e.g. an in-store-only merchant whose channel choice alone
        // satisfies the whole checklist) — nothing left for this modal to walk them through, so
        // never show it, now or on a future visit.
        if (progress.completedCount >= progress.totalCount) {
          this.markSeen();
          return;
        }
        this.visible = true;
      },
      error: () => {
        this.isLoadingProgress = false;
        this.visible = true;
      }
    });
  }

  /** Store domain & template / Shipping only apply to merchants selling online, and Billing card
   *  only applies to a paid plan (Free is never billed) — same signals the checklist page and
   *  topbar badge use to drop the step count. Filtered independently: an online-selling Free-plan
   *  merchant still needs domain/shipping but not billing card. Defaults to showing all 7 until
   *  progress has loaded, to avoid a layout jump. */
  get visibleSteps(): WelcomeSetupStep[] {
    if (!this.progress) {
      return this.steps;
    }
    return this.steps.filter((step) => {
      if (!this.progress.sellsOnline && (step.key === 'domain' || step.key === 'shipping')) return false;
      if (!this.progress.cardStepNeeded && step.key === 'card') return false;
      return true;
    });
  }

  private isBusinessInfoMissing(): boolean {
    const setup: any = GlobalService.userInfo?.businessSetup?.[0];
    return !(setup?.address && setup?.city && setup?.state && (setup?.zip || setup?.zipCode));
  }

  isStepComplete(step: WelcomeSetupStep): boolean {
    if (!this.progress) return false;
    switch (step.key) {
      case 'channels': return this.progress.channelsComplete;
      case 'business': return this.progress.businessComplete;
      case 'domain': return this.progress.domainComplete;
      case 'product': return this.progress.productsComplete;
      case 'shipping': return this.progress.shippingComplete;
      case 'stripe': return this.progress.stripeComplete;
      case 'card': return this.progress.cardComplete;
      default: return false;
    }
  }

  get displayName(): string {
    const firstName = GlobalService.userInfo?.firstName || '';
    const lastName = GlobalService.userInfo?.lastName || '';
    return `${firstName} ${lastName}`.trim();
  }

  dismiss(): void {
    this.markSeen();
    this.visible = false;
  }

  startSetup(): void {
    this.markSeen();
    this.visible = false;
    this.router.navigate(['/app/business-setup']);
  }

  // TODO re-enable: welcome setup guide is hidden for this specific user — they don't need
  // business setup (same convention as business.component.ts's isShippingConfigV2Hidden()/
  // isTaxRateNewHidden()).
  private isSetupGuideHiddenForUser(): boolean {
    return this.isCurrentUser('younksnutritioncentral@yahoo.com') || this.isAdminRole();
  }

  /** Role 1001 (admin) skips the setup guide entirely, so its progress requests
   *  (products, shipping, paymentconfig, GetConnectStatus) are never fired for admin. */
  private isAdminRole(): boolean {
    const roles: any[] = GlobalService.userInfo?.role ?? GlobalService.userInfo?.Role ?? [];
    return Array.isArray(roles) && roles.some((r) => String(r) === '1001');
  }

  private isCurrentUser(email: string): boolean {
    const identity = String(
      GlobalService.userInfo?.username
      || GlobalService.userInfo?.userName
      || GlobalService.userInfo?.email
      || ''
    ).trim().toLowerCase();
    return identity === email;
  }

  private currentUserKey(): string {
    return (
      GlobalService.userInfo?.userID ||
      GlobalService.userInfo?.userId ||
      GlobalService.userInfo?.username ||
      'anonymous'
    ).toString();
  }

  private hasBeenSeen(): boolean {
    return localStorage.getItem(this.storageKeyPrefix + this.currentUserKey()) === '1';
  }

  private markSeen(): void {
    localStorage.setItem(this.storageKeyPrefix + this.currentUserKey(), '1');
  }
}
